import { useState, useRef } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';

export function AddItemModal({ isOpen, onClose, onAdd, categories }) {
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [errors, setErrors] = useState({});
  const summaryRef = useRef(null);

  if (!isOpen) return null;

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    const nextItemName = itemName.trim();
    const nextQuantity = quantity.trim();
    const nextCustomCategory = customCategory.trim();

    if (!nextItemName) nextErrors.itemName = 'Item name is required.';
    if (!nextQuantity) nextErrors.quantity = 'Quantity is required.';
    if (isCustomCategory && !nextCustomCategory) nextErrors.customCategory = 'Category name is required.';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      focusSummary();
      return;
    }

    onAdd(nextItemName, nextQuantity, isCustomCategory ? nextCustomCategory : selectedCategory);
    setItemName('');
    setQuantity('');
    setSelectedCategory(categories[0]);
    setIsCustomCategory(false);
    setCustomCategory('');
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    // Reset form on close
    setItemName('');
    setQuantity('');
    setSelectedCategory(categories[0]);
    setIsCustomCategory(false);
    setCustomCategory('');
    setErrors({});
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />
      
      {/* Drawer */}
      <div 
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="max-w-md mx-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          <div className="px-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl">Add Item</h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              {Object.keys(errors).length > 0 && (
                <div
                  ref={summaryRef}
                  tabIndex={-1}
                  role="alert"
                  aria-live="assertive"
                  className="mb-4 rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 outline-none focus:ring-2 focus:ring-[#B85A5A]"
                >
                  <div className="flex items-start gap-2 text-[#B85A5A]">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium">Please fix the highlighted fields below.</p>
                      {errors.itemName && <p>{errors.itemName}</p>}
                      {errors.quantity && <p>{errors.quantity}</p>}
                      {errors.customCategory && <p>{errors.customCategory}</p>}
                    </div>
                  </div>
                </div>
              )}
              {/* Item Name */}
              <div className="mb-4">
                <label htmlFor="itemName" className="block text-gray-700 mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  id="itemName"
                  value={itemName}
                  onChange={(e) => {
                    setItemName(e.target.value);
                    clearError('itemName');
                  }}
                  placeholder="e.g. Tomatoes"
                  aria-invalid={!!errors.itemName}
                  aria-describedby={errors.itemName ? 'item-name-error' : undefined}
                  className={`w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none ${errors.itemName ? 'ring-2 ring-[#B85A5A]' : ''}`}
                />
                {errors.itemName && (
                  <div id="item-name-error" className="mt-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                    <span>{errors.itemName}</span>
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div className="mb-4">
                <label htmlFor="quantity" className="block text-gray-700 mb-2">
                  Quantity
                </label>
                <input
                  type="text"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    clearError('quantity');
                  }}
                  placeholder="e.g. 500g or 3 items"
                  aria-invalid={!!errors.quantity}
                  aria-describedby={errors.quantity ? 'item-quantity-error' : undefined}
                  className={`w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none ${errors.quantity ? 'ring-2 ring-[#B85A5A]' : ''}`}
                />
                {errors.quantity && (
                  <div id="item-quantity-error" className="mt-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                    <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                    <span>{errors.quantity}</span>
                  </div>
                )}
              </div>

              {/* Category */}
              <div className="mb-6">
                <label htmlFor="category" className="block text-gray-700 mb-2">
                  Category
                </label>
                {!isCustomCategory ? (
                  <>
                    <select
                      id="category"
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        clearError('customCategory');
                      }}
                      className="w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none"
                    >
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setIsCustomCategory(true)}
                      className="mt-2 text-primary text-sm flex items-center gap-1 hover:text-primary-hover transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add New Category</span>
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => {
                        setCustomCategory(e.target.value);
                        clearError('customCategory');
                      }}
                      placeholder="Enter category name"
                      aria-invalid={!!errors.customCategory}
                      aria-describedby={errors.customCategory ? 'custom-category-error' : undefined}
                      className={`w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none ${errors.customCategory ? 'ring-2 ring-[#B85A5A]' : ''}`}
                    />
                    {errors.customCategory && (
                      <div id="custom-category-error" className="flex items-center gap-1.5 text-sm text-[#B85A5A]">
                        <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                        <span>{errors.customCategory}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomCategory(false);
                        setCustomCategory('');
                        clearError('customCategory');
                      }}
                      className="text-muted-foreground text-sm hover:text-foreground transition-colors"
                    >
                      Use existing category
                    </button>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-3 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] bg-card hover:bg-[var(--primary-light)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors"
                >
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
